/**
 * WebSocket 连接管理 Hook
 *
 * 统一处理 WebSocket 单例连接、断线重连、消息分发
 *
 * 注意: 此 Hook 使用的是服务器与 Web 客户端之间的 WebSocket 协议 (ver: 1.0)
 * 终端通信协议 (ver: 1.0.0_20260105) 由后端处理
 */

import { useCallback, useEffect } from 'react'
import {
  useDeviceStore,
  useUIStore,
  useUserStore,
  TelemetryData,
  LockState,
} from '@/store/useDeviceStore'

// WebSocket 服务器地址
// Docker 环境：使用当前页面的主机名（通过 Vite 代理）
// 生产环境：使用配置的完整 URL
const getWsUrl = () => {
  const configUrl = import.meta.env.VITE_WS_URL
  if (configUrl) {
    return configUrl
  }

  // 使用当前页面的主机名构建 WebSocket URL
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  const host = window.location.host
  return `${protocol}//${host}/ws/monitor`
}

const WS_URL = getWsUrl()

// 重连配置
const RECONNECT_INTERVAL = 3000 // 重连间隔（毫秒）
const MAX_RECONNECT_ATTEMPTS = 10 // 最大重连次数

interface WebSocketMessage {
  ver: string
  type: string
  ts: number
  data?: TelemetryData
  alarm_info?: {
    id: number
    dev_id: string
    level: 'warning' | 'danger'
    code: number
    msg: string
    target_geo?: {
      lon: number
      lat: number
      alt?: number
    } | null
    created_at: string
    handled: boolean
  }
  dev_id?: string
  lock_state?: LockState
  recording?: {
    vis: boolean
    ir: boolean
  }
}

interface WebSocketAuthContext {
  token: string
  userId: string | null
  role: 'admin' | 'user' | null
  username: string | null
}

interface DisconnectOptions {
  clearSubscriptions?: boolean
}

let ws: WebSocket | null = null
let reconnectAttempts = 0
let reconnectTimer: ReturnType<typeof setTimeout> | null = null
let shouldReconnect = false
let authContext: WebSocketAuthContext | null = null
const subscribedDevIds = new Set<string>()

const clearReconnectTimer = () => {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
}

const setConnectionState = (connected: boolean) => {
  useDeviceStore.getState().setWsConnected(connected)
}

const sendJson = (data: object, warnIfDisconnected = true) => {
  if (ws?.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify(data))
    return true
  }

  if (warnIfDisconnected) {
    console.warn('WebSocket 未连接，无法发送消息')
  }
  return false
}

const sendAuthMessage = () => {
  if (!authContext) {
    return
  }

  sendJson({
    ver: '1.0',
    type: 'auth',
    token: authContext.token,
    data: {
      user_id: authContext.userId,
      role: authContext.role,
      username: authContext.username,
    },
  })
}

const syncSubscriptions = () => {
  if (subscribedDevIds.size === 0) {
    return
  }

  sendJson({
    ver: '1.0',
    type: 'subscribe',
    data: { dev_ids: Array.from(subscribedDevIds) },
  })
}

const handleMessage = (event: MessageEvent) => {
  try {
    const msg: WebSocketMessage = JSON.parse(event.data)
    const deviceStore = useDeviceStore.getState()
    const uiStore = useUIStore.getState()

    switch (msg.type) {
      case 'push_telemetry':
        if (msg.data) {
          deviceStore.updateTelemetry(msg.data)
        }
        break

      case 'push_alarm':
        if (msg.alarm_info) {
          uiStore.addAlarm({
            id: msg.alarm_info.id,
            dev_id: msg.alarm_info.dev_id,
            level: msg.alarm_info.level,
            code: msg.alarm_info.code,
            msg: msg.alarm_info.msg,
            created_at: msg.alarm_info.created_at,
            handled: msg.alarm_info.handled,
            target_geo: msg.alarm_info.target_geo,
          })
        }
        break

      case 'push_lock_status':
        if (msg.dev_id && msg.lock_state) {
          deviceStore.updateLockState(msg.dev_id, msg.lock_state)
        }
        break

      case 'push_recording_status':
        if (msg.dev_id && msg.recording) {
          deviceStore.updateRecordingState(msg.dev_id, msg.recording)
        }
        break

      case 'resp_auth':
      case 'resp_subscribe':
      case 'resp_control':
      case 'resp_lock':
        console.log('WebSocket 响应:', msg)
        break

      default:
        console.warn('未知消息类型:', msg.type)
    }
  } catch (error) {
    console.error('解析 WebSocket 消息失败:', error)
  }
}

const scheduleReconnect = () => {
  if (!shouldReconnect || !authContext?.token) {
    return
  }

  if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
    console.error('WebSocket 重连次数已达上限')
    return
  }

  reconnectAttempts += 1
  console.log(
    `将在 ${RECONNECT_INTERVAL}ms 后重连 (${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`
  )
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null
    openConnection()
  }, RECONNECT_INTERVAL)
}

const openConnection = () => {
  if (!authContext?.token) {
    setConnectionState(false)
    return
  }

  if (ws?.readyState === WebSocket.OPEN || ws?.readyState === WebSocket.CONNECTING) {
    return
  }

  clearReconnectTimer()
  console.log('正在连接 WebSocket:', WS_URL)

  const socket = new WebSocket(WS_URL)
  ws = socket

  socket.onopen = () => {
    if (ws !== socket) {
      return
    }

    console.log('WebSocket 已连接')
    setConnectionState(true)
    reconnectAttempts = 0
    sendAuthMessage()
    syncSubscriptions()
  }

  socket.onmessage = handleMessage

  socket.onclose = (event) => {
    if (ws !== socket) {
      return
    }

    console.log('WebSocket 已断开:', event.code, event.reason)
    ws = null
    setConnectionState(false)
    scheduleReconnect()
  }

  socket.onerror = (error) => {
    if (ws !== socket) {
      return
    }

    console.error('WebSocket 错误:', error)
  }
}

const closeConnection = ({ clearSubscriptions = false }: DisconnectOptions = {}) => {
  shouldReconnect = false
  reconnectAttempts = 0
  clearReconnectTimer()

  if (clearSubscriptions) {
    subscribedDevIds.clear()
  }

  const socket = ws
  ws = null

  if (
    socket &&
    (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)
  ) {
    socket.close()
  }

  setConnectionState(false)
}

const connectWebSocket = (nextAuth: WebSocketAuthContext) => {
  const authChanged =
    authContext?.token !== nextAuth.token ||
    authContext?.userId !== nextAuth.userId ||
    authContext?.role !== nextAuth.role ||
    authContext?.username !== nextAuth.username

  authContext = nextAuth
  shouldReconnect = true

  if (authChanged && ws) {
    const socket = ws
    ws = null
    clearReconnectTimer()
    reconnectAttempts = 0
    socket.close()
  }

  openConnection()
}

const disconnectWebSocket = (options?: DisconnectOptions) => {
  authContext = null
  closeConnection(options)
}

export function useWebSocketLifecycle(
  token: string | null,
  userId: string | null,
  role: 'admin' | 'user' | null,
  username: string | null
) {
  useEffect(() => {
    if (!token) {
      disconnectWebSocket({ clearSubscriptions: true })
      return
    }

    connectWebSocket({
      token,
      userId,
      role,
      username,
    })
  }, [token, userId, role, username])

  useEffect(() => {
    return () => {
      disconnectWebSocket({ clearSubscriptions: true })
    }
  }, [])
}

export function useWebSocket() {
  /**
   * 发送消息
   */
  const send = useCallback((data: object) => {
    return sendJson(data)
  }, [])

  /**
   * 订阅设备
   */
  const subscribe = useCallback((devIds: string[]) => {
    const normalizedDevIds = Array.from(new Set(devIds.filter(Boolean)))

    if (normalizedDevIds.length === 0) {
      return
    }

    normalizedDevIds.forEach((devId) => {
      subscribedDevIds.add(devId)
    })

    sendJson({
      ver: '1.0',
      type: 'subscribe',
      data: { dev_ids: normalizedDevIds },
    }, false)
  }, [])

  /**
   * 取消订阅
   */
  const unsubscribe = useCallback((devIds: string[]) => {
    const normalizedDevIds = Array.from(new Set(devIds.filter(Boolean)))

    if (normalizedDevIds.length === 0) {
      return
    }

    normalizedDevIds.forEach((devId) => {
      subscribedDevIds.delete(devId)
    })

    sendJson({
      ver: '1.0',
      type: 'unsubscribe',
      data: { dev_ids: normalizedDevIds },
    }, false)
  }, [])

  /**
   * 发送控制指令
   */
  const sendControl = useCallback((devId: string, params: object) => {
    send({
      ver: '1.0',
      type: 'req_control',
      ts: Date.now(),
      token: authContext?.token,
      data: {
        dev_id: devId,
        cmd: 'set_params',
        params,
      },
    })
  }, [send])

  /**
   * 申请控制权
   */
  const acquireLock = useCallback((devId: string) => {
    const { username } = useUserStore.getState()
    send({
      ver: '1.0',
      type: 'req_lock',
      data: {
        dev_id: devId,
        action: 'acquire',
        owner_name: username,
      },
    })
  }, [send])

  /**
   * 释放控制权
   */
  const releaseLock = useCallback((devId: string) => {
    send({
      ver: '1.0',
      type: 'req_lock',
      data: {
        dev_id: devId,
        action: 'release',
      },
    })
  }, [send])

  const reconnect = useCallback(() => {
    if (!authContext) {
      return
    }

    const currentAuth = authContext
    closeConnection()
    connectWebSocket(currentAuth)
  }, [])

  return {
    send,
    subscribe,
    unsubscribe,
    sendControl,
    acquireLock,
    releaseLock,
    reconnect,
  }
}

export default useWebSocket
